/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, useCallback, useState, useContext, FC, useEffect } from 'react';
import useEscrowAnalysisCalculator from '../common/hooks/useEscrowAnalysisCalculator';
import useESignDocTrackingList from '../common/hooks/useESignDocTrackingList';
import {
  formatAsDatabaseDate,
  formatDateYYYYMMDD,
  getDateValue,
  getTimestampAsDate,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
} from '../common/UtilityFunctions';
import { useLoanContext } from './LoanContext';
import useESign from '../common/hooks/useESign';
import useMaxmind from '../common/hooks/useMaxmind';
import useIPData from '../common/hooks/useIPData';
import useGeolocationData from '../common/hooks/useGeolocation';
import useBorrowerData from '../common/hooks/useBorrowerData';
import useAISignSend from '../common/hooks/useAISignSend';
import { useAuthContext } from './AuthContext';
import { Button } from '@mui/material';
import Styles from '../css/GoGreenPage.module.css';
import useApplicationList from '../common/hooks/useApplicationList';

export enum Status {
  SUCCESS = 'Success',
  ERROR = 'Error',
  LOADING = 'Loading',
  READY = '',
  CONFIRMING = 'Confirmation',
}

export enum Action {
  OPTOUT = 'optout',
  SIGN = 'sign',
  OPEN = 'open',
  SENDEMAIL = 'sendemail',
  NOACTION = '',
}

// Judith Espinal - SQC-766
/** Same enrollment flag as the loan borrower record. */
export function isBorrowerGoGreenEnrolled(borrower: BorrowerData | null | undefined): boolean {
  return Boolean(borrower?.goGreen);
}

/** Documents Go Green CTA when the target borrower is not enrolled (any viewing role). */
export function shouldShowDocumentsGoGreenSignup(borrower: BorrowerData | null | undefined): boolean {
  if (borrower == null) return false;
  return !isBorrowerGoGreenEnrolled(borrower);
}

type LocationData = {
  ip: string;
  latitude: string;
  longitude: string;
  timeZone: string;
};

function getInitialSentEmailToBorrowerRecord(borrowerDataArr: BorrowerData[]): Record<string, boolean> {
  let sentEmailsRecord: Record<string, boolean> = {};
  borrowerDataArr?.forEach((borrower) => {
    sentEmailsRecord[borrower.borrowerNumber] = false;
  });
  return sentEmailsRecord;
}

type GoGreenContextType = {
  getDocumentDataBase: () => ESignDocumentDataRequest;
  borrowerNumber: '';
  setBorrowerNumber: (val: string) => void;
  isESignCompleted: (esignReq: ESignRequest, esignRes: UnknownESignResponse) => boolean;
  getESignOptOutRequest: () => ESignOptOutRequest;
  processESignResponse: (esignReq: ESignRequest, esignRes: UnknownESignResponse) => void;
  status: Status;
  setStatus: (val: Status) => void;
  actionInProcess: Action;
  setActionInProcess: (val: Action) => void;
  processGoGreenRequest: () => void;
  getESignSignRequest: () => Promise<ESignSignRequest>;
  finishESignProcess: () => void;
  startESignProcess: (borrowerNumber: string) => void;
  currentBorrower: BorrowerData;
  reloadBorrowerData: () => void;
  fetchingBorrowerData: boolean;
  borrowerData: Record<string, BorrowerData>;
  borrowerDataArr: Array<BorrowerData>;
  modalButtons: () => React.JSX.Element;
  sentEmailsToBorrowersRecord: Record<string, boolean>;
  esignDocTrackingList: UnknownESignDocTrackingListResponse;
  isFetchingESignDocTrackingList: boolean;
  loadingApplicationList: boolean;
};

const GoGreenContext = createContext<GoGreenContextType | undefined>(undefined);

const useGoGreenContext = () => {
  const goGreenContext = useContext(GoGreenContext);
  if (goGreenContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('goGreenContext must be inside a GoGreenProvider');
  }
  return goGreenContext;
};

const GoGreenProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [borrowerNumber, setBorrowerNumber] = useState(null);
  const { currentBorrower, reloadBorrowerData, fetchingBorrowerData, borrowerData, borrowerDataArr } =
    useBorrowerData();
  const { sendAISign } = useAISignSend();
  const { servicerUser } = useAuthContext();
  const { applicationList, loadingApplicationList } = useApplicationList('borrowerNumber,applicationNumber');
  //statuses
  const [actionInProcess, setActionInProcess] = useState<Action>(Action.NOACTION);
  const [status, setStatus] = useState<Status>(Status.READY);
  const { loanNumber } = useLoanContext();
  const { processESignRequest } = useESign();
  const { processMaxmind } = useMaxmind();
  const { processIP } = useIPData();
  const { processGeolocation } = useGeolocationData();
  const [sentEmailsToBorrowersRecord, setSentEmailsToBorrowerRecord] = useState<Record<string, boolean>>(
    getInitialSentEmailToBorrowerRecord(borrowerDataArr),
  );
  const { esignDocTrackingList, reloadESignDocTrackingList, isFetchingESignDocTrackingList } =
    useESignDocTrackingList();
  const getDocumentDataBase = useCallback(() => {
    const documentDataBase: ESignDocumentDataRequest = {
      loanNo: loanNumber,
      brwrNo: borrowerNumber,
      formName: 'econsent',
      webAppName: 'redcarpetservice',
    };
    return documentDataBase;
  }, [loanNumber, borrowerNumber]);

  useEffect(() => {
    setSentEmailsToBorrowerRecord(getInitialSentEmailToBorrowerRecord(borrowerDataArr));
  }, [borrowerDataArr]);

  const isESignCompleted = useCallback((esignReq: ESignRequest, esignRes: UnknownESignResponse): boolean => {
    if (isObjEmptyOrNull(esignRes) || isObjEmptyOrNull(esignReq) || !esignRes.documentData || !esignReq.documentData) {
      return false;
    }

    const esignResDocData = esignRes.documentData;
    const esignReqDocData = esignReq.documentData;
    if (esignResDocData.brwrNo !== esignReqDocData.brwrNo) return false;
    if (esignResDocData.loanNo !== esignReqDocData.loanNo) return false;
    if (esignResDocData.formName.toUpperCase() !== esignReqDocData.formName.toUpperCase()) return false;
    if (esignReq.requestedAction === Action.SIGN && esignResDocData.webAppName !== esignReqDocData.webAppName)
      return false;
    if ('brwrIP' in esignReqDocData && esignResDocData.brwrIP !== esignReqDocData.brwrIP) return false;
    if ('timezone' in esignReqDocData && esignResDocData.timezone !== esignReqDocData.timezone) return false;
    if ('location' in esignReqDocData && esignResDocData.location !== esignReqDocData.location) return false;
    if ('browser' in esignReqDocData && esignResDocData.browser !== esignReqDocData.browser) return false;
    if ('operatingSystem' in esignReqDocData && esignResDocData.operatingSystem !== esignReqDocData.operatingSystem)
      return false;
    return true;
  }, []);

  const getESignOptOutRequest = useCallback(() => {
    const esignReq: ESignOptOutRequest = {
      requestedAction: Action.OPTOUT,
      documentData: getDocumentDataBase(),
    };
    return esignReq;
  }, [getDocumentDataBase]);

  const processESignResponse = useCallback(
    (esignReq: ESignRequest, esignRes: UnknownESignResponse) => {
      const esigncompleted = isESignCompleted(esignReq, esignRes);
      esigncompleted ? setStatus(Status.SUCCESS) : setStatus(Status.ERROR);
    },
    [isESignCompleted],
  );

  const getUserLocationData = useCallback(async (): Promise<LocationData> => {
    const ipResp = await processIP();
    const position = await processGeolocation();
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    if (!position || !ipResp || !timeZone) {
      return null;
    }
    return {
      ip: ipResp.ip,
      latitude: position.coords.latitude.toString(),
      longitude: position.coords.longitude.toString(),
      timeZone: timeZone,
    };
  }, [processMaxmind, processGeolocation, processIP]);

  const getDocumentDataSignObj = useCallback((locationData: LocationData, maxmindRes: maxmindResponse) => {
    return {
      timezone: locationData ? locationData.timeZone : maxmindRes.userLocation.timeZone,
      location: locationData
        ? locationData.latitude + ', ' + locationData.longitude
        : maxmindRes.userLocation.latitude + ', ' + maxmindRes.userLocation.longitude,
      browser: navigator.userAgent,
      operatingSystem: navigator.userAgent,
      brwrIP: locationData ? locationData.ip : maxmindRes.userIp,
      emailSignedFormToBorrower: true,
    };
  }, []);

  const getESignSignRequest = useCallback(async () => {
    const maxmindRes = await processMaxmind();
    let locationData = null;
    if (isObjEmptyOrNull(maxmindRes)) locationData = await getUserLocationData();
    if (isObjEmptyOrNull(maxmindRes) && isObjEmptyOrNull(locationData)) return null;

    const documentDataSign = getDocumentDataSignObj(locationData, maxmindRes);
    const documentDataBase = getDocumentDataBase();
    const docData: ESignDocumentDataSignRequest = { ...documentDataBase, ...documentDataSign };
    const esignReq: ESignSignRequest = {
      requestedAction: Action.SIGN,
      documentData: docData,
    };
    return esignReq;
  }, [processMaxmind, getDocumentDataBase]);

  const processESign = useCallback(async () => {
    if (actionInProcess === Action.SIGN || actionInProcess === Action.OPTOUT) {
      try {
        const esignReq = actionInProcess === Action.SIGN ? await getESignSignRequest() : getESignOptOutRequest();
        const esignRes = await processESignRequest(esignReq);
        processESignResponse(esignReq, esignRes);
        reloadBorrowerData();
      } catch (e) {
        setStatus(Status.ERROR);
      }
    }
  }, [
    actionInProcess,
    processESignRequest,
    getESignSignRequest,
    getESignOptOutRequest,
    processESignResponse,
    reloadBorrowerData,
  ]);

  const getApplicationNumber = useCallback(() => {
    const found = applicationList.find((element) =>
      element.borrowerNumber ? element.borrowerNumber == borrowerNumber : false,
    );
    if (found) {
      return found.applicationNumber ? found.applicationNumber : '01';
    }
    return '01';
  }, [applicationList, borrowerNumber]);

  const processSendEmail = useCallback(async () => {
    if (actionInProcess === Action.SENDEMAIL) {
      try {
        const emailParams = {
          docId: 'ECONSE',
          imagingFormName: 'GO GREEN',
          borrowerRecipients: [
            {
              applicationNumber: getApplicationNumber(),
              borrowerNumber: borrowerNumber,
              signer: true,
              routingOrder: 1,
              recipientType: 'Signer',
              customEmailTemplateName: 'Servicing Go Green',
            },
          ],
        };
        const resp = await sendAISign(emailParams);
        !isObjEmptyOrNull(resp) ? setStatus(Status.SUCCESS) : setStatus(Status.ERROR);
      } catch (e) {
        setStatus(Status.ERROR);
      }
    }
  }, [actionInProcess, sendAISign, borrowerNumber, getApplicationNumber]);

  const processGoGreenRequest = useCallback(async () => {
    setStatus(Status.LOADING);
    processESign();
    processSendEmail();
  }, [processESign, processSendEmail]);

  const finishSendEmail = useCallback(() => {
    if (status === Status.SUCCESS && actionInProcess === Action.SENDEMAIL) {
      const newRec = { ...sentEmailsToBorrowersRecord };
      newRec[borrowerNumber] = true;
      setSentEmailsToBorrowerRecord(newRec);
      reloadESignDocTrackingList();
    }
  }, [status, actionInProcess, sentEmailsToBorrowersRecord, reloadESignDocTrackingList]);

  const finishOptOut = useCallback(() => {
    if (status === Status.SUCCESS && actionInProcess === Action.OPTOUT) {
      const newRec = { ...sentEmailsToBorrowersRecord };
      newRec[borrowerNumber] = false;
      setSentEmailsToBorrowerRecord(newRec);
      reloadESignDocTrackingList();
    }
  }, [status, actionInProcess, sentEmailsToBorrowersRecord, reloadESignDocTrackingList]);

  const finishESignProcess = useCallback(() => {
    finishSendEmail();
    finishOptOut();
    setStatus(Status.READY);
    setActionInProcess(Action.NOACTION);
    reloadBorrowerData();
  }, [reloadBorrowerData, finishSendEmail, finishOptOut]);

  const startESignProcess = useCallback(
    (borrowerNumber: string) => {
      setBorrowerNumber(borrowerNumber);
      setStatus(Status.CONFIRMING);
      if (borrowerData[borrowerNumber]?.goGreen) {
        setActionInProcess(Action.OPTOUT);
      } else {
        servicerUser ? setActionInProcess(Action.SENDEMAIL) : setActionInProcess(Action.SIGN);
      }
    },
    [borrowerData, servicerUser],
  );

  const modalButtons = () => {
    return (
      <>
        {(status === Status.ERROR || status === Status.SUCCESS) && (
          <Button
            fullWidth
            variant='contained'
            onClick={() => {
              finishESignProcess();
            }}
            className={status === Status.SUCCESS ? Styles.primbutton : Styles.secondarybutton}
          >
            {'I Understand'}
          </Button>
        )}
        {status === Status.ERROR && (
          <Button
            fullWidth
            variant='contained'
            onClick={() => {
              processGoGreenRequest();
            }}
            className={Styles.primbutton}
          >
            {'Try Again'}
          </Button>
        )}
        {status === Status.CONFIRMING && actionInProcess === Action.OPTOUT && (
          <>
            <Button
              fullWidth
              variant='contained'
              onClick={() => {
                finishESignProcess();
              }}
              className={Styles.secondarybutton}
            >
              {'Cancel'}
            </Button>
            <Button
              fullWidth
              variant='contained'
              onClick={() => {
                processGoGreenRequest();
              }}
              className={Styles.redbutton}
            >
              {'Opt out'}
            </Button>
          </>
        )}
        {status === Status.CONFIRMING && actionInProcess === Action.SENDEMAIL && (
          <>
            <Button
              fullWidth
              variant='contained'
              onClick={() => {
                finishESignProcess();
              }}
              className={Styles.secondarybutton}
            >
              {'Cancel'}
            </Button>
            <Button
              fullWidth
              variant='contained'
              onClick={() => {
                processGoGreenRequest();
              }}
              className={Styles.primbutton}
            >
              {'Send email'}
            </Button>
          </>
        )}
      </>
    );
  };

  return (
    <GoGreenContext.Provider
      value={{
        currentBorrower,
        reloadBorrowerData,
        fetchingBorrowerData,
        getDocumentDataBase,
        borrowerNumber,
        setBorrowerNumber,
        isESignCompleted,
        getESignOptOutRequest,
        processESignResponse,
        status,
        setStatus,
        actionInProcess,
        setActionInProcess,
        processGoGreenRequest,
        getESignSignRequest,
        finishESignProcess,
        startESignProcess,
        borrowerData,
        borrowerDataArr,
        modalButtons,
        sentEmailsToBorrowersRecord,
        esignDocTrackingList,
        isFetchingESignDocTrackingList,
        loadingApplicationList,
      }}
    >
      {children}
    </GoGreenContext.Provider>
  );
};

export { GoGreenProvider, useGoGreenContext };
